import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, keyframes } from "@mui/material/styles";

import {
  ACCENT,
  FRAME,
  SITE_STEPS,
  marketingPalette,
} from "@/components/marketing/tokens";
import NavAnchor from "./NavAnchor";
import { LaunchIcon } from "./navIcons";
import {
  CapsHeading,
  CompactRow,
  FeatureRow,
  PlainRow,
  type RowVariant,
} from "./NavRows";
import type { NavBlock, NavColumn, NavFooter, NavMenu } from "./navigation";
import { COMPACT_TONES, PANEL_MOTION } from "./tokens";

/** The pale rule around the menu footer's accent button. */
const ACCENT_EDGE = "#EBF7F5";

/** What the drawer changes about the body's own layout. */
const BODY = {
  panel: { gap: "12px" },
  drawer: { gap: 0 },
};

const COLUMNS_ROW = {
  panel: { flexDirection: "row", columnGap: "32px", m: "16px 0" },
  drawer: { flexDirection: "column", rowGap: 2, m: "8px 0 0" },
};

/** The drawer packs rows 9.6px apart inside a column, 8px between columns. */
const COLUMN = { panel: { rowGap: "28px" }, drawer: { rowGap: "9.6px" } };

/**
 * Where a column's second list moves beside its own. The panel's third column
 * holds two only once the panel is at its full frame, where each is the
 * 202.67px the design draws; under that the design stacks them, as the
 * published menu always did. The drawer has room for both from the site's
 * smallest step.
 */
const SIDE_BY_SIDE = {
  panel: `@media (min-width: ${FRAME.header}px)`,
  drawer: `@media (min-width: ${SITE_STEPS.tiny}px)`,
};

const LIST_GAP = "16px";

/** The rule under every column heading, and above a menu's footer button. */
function Rule() {
  return (
    <Box sx={{ height: "0.96px", backgroundColor: marketingPalette.wash }} />
  );
}

function Block({
  block,
  variant,
  onNavigate,
}: {
  block: NavBlock;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  if (block.kind === "features") {
    return block.items.map((link) => (
      <FeatureRow
        key={link.href}
        link={link}
        variant={variant}
        onNavigate={onNavigate}
      />
    ));
  }
  if (block.kind === "compact") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {block.items.map((link) => (
          <CompactRow
            key={link.href}
            link={link}
            variant={variant}
            onNavigate={onNavigate}
          />
        ))}
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {block.items.map((link) => (
        <PlainRow
          key={link.href}
          link={link}
          variant={variant}
          onNavigate={onNavigate}
        />
      ))}
    </Box>
  );
}

/** The drawer's button spans the row and centres what the panel left-aligns. */
const FOOTER_BUTTON = {
  panel: {
    alignSelf: "flex-start",
    p: "12px 16px",
    fontSize: 16,
    lineHeight: "24px",
  },
  drawer: {
    alignSelf: "stretch",
    justifyContent: "center",
    p: "8px 16px",
    fontSize: 14.08,
    lineHeight: "21.12px",
  },
};

function FooterButton({
  footer,
  variant,
  onNavigate,
}: {
  footer: Extract<NavFooter, { kind: "button" }>;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Rule />
      <NavAnchor
        href={footer.href}
        onNavigate={onNavigate}
        sx={{
          ...FOOTER_BUTTON[variant],
          display: "flex",
          alignItems: "center",
          gap: "12px",
          borderRadius: "16px",
          border: `1px solid ${ACCENT_EDGE}`,
          backgroundColor: alpha(ACCENT, 0.1),
          transition: "border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
          "&:hover": { borderColor: "primary.main" },
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: "inherit", lineHeight: "inherit" }}
        >
          {footer.label}
        </Typography>
        <Box
          component="img"
          src="/marketing/nav/arrow.svg"
          alt=""
          sx={{ width: "14.08px", height: "14.08px" }}
        />
      </NavAnchor>
    </Box>
  );
}

/** The three numbers the drawer's banner tightens. */
const BANNER = {
  panel: { pad: "9.6px", gap: 3, summaryLeading: "21.12px" },
  drawer: { pad: "8px", gap: "12.8px", summaryLeading: "19.712px" },
};

function FooterBanner({
  footer,
  variant,
  onNavigate,
}: {
  footer: Extract<NavFooter, { kind: "banner" }>;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  const banner = BANNER[variant];
  return (
    <NavAnchor
      href={footer.href}
      onNavigate={onNavigate}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        maxWidth: 1,
        overflow: "hidden",
        p: banner.pad,
        borderRadius: "8px",
        border: `1px solid ${alpha(marketingPalette.ink, 0.2)}`,
        transition: "border-color 0.2s ease",
        "&:hover": { borderColor: alpha(marketingPalette.ink, 0.4) },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: banner.gap }}>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: 1,
            border: `1px solid ${COMPACT_TONES.violet.hover}`,
            backgroundColor: alpha(COMPACT_TONES.violet.hover, 0.12),
          }}
        >
          <Box
            component="img"
            src="/marketing/nav/compare-products.svg"
            alt=""
            sx={{ width: "45.6px", height: "45.6px" }}
          />
        </Box>
        <Box>
          <Typography
            component="p"
            sx={{ fontSize: 15.04, lineHeight: "22px" }}
          >
            {footer.label}
          </Typography>
          <Typography
            component="p"
            sx={{
              fontSize: 14.08,
              lineHeight: banner.summaryLeading,
              color: marketingPalette.inkSubtle,
              opacity: 0.7,
            }}
          >
            {footer.summary}
          </Typography>
        </Box>
      </Box>
      <LaunchIcon sx={{ fontSize: "22.08px" }} />
    </NavAnchor>
  );
}

function MenuFooter({
  footer,
  variant,
  onNavigate,
}: {
  footer: NavFooter;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  return footer.kind === "button" ? (
    <FooterButton footer={footer} variant={variant} onNavigate={onNavigate} />
  ) : (
    <FooterBanner footer={footer} variant={variant} onNavigate={onNavigate} />
  );
}

const rise = keyframes({
  from: { opacity: 0, transform: `translateY(-${PANEL_MOTION.rise}px)` },
  to: { opacity: 1, transform: "none" },
});

/**
 * The published panel arrives in waves: the columns fade up one after another
 * once the curtain is on its way down. The drawer fades its body as one
 * (`DrawerMenu`), so none of this applies there.
 */
function columnMotion(index: number, variant: RowVariant) {
  if (variant === "drawer") {
    return undefined;
  }
  const delay = PANEL_MOTION.columnLead + PANEL_MOTION.columnStep * index;
  return {
    animation: `${rise} ${PANEL_MOTION.duration}ms ease-out ${delay}ms both`,
  };
}

/** A heading over its rule; a second list's shows only once it is beside the first. */
function ColumnHeading({
  heading,
  beside,
}: {
  heading: string;
  beside?: true;
}) {
  return (
    <Box
      sx={{
        width: 1,
        display: beside ? "none" : "flex",
        flexDirection: "column",
        rowGap: 2,
        ...(beside ? { [SIDE_BY_SIDE.panel]: { display: "flex" } } : null),
      }}
    >
      <CapsHeading>{heading}</CapsHeading>
      <Rule />
    </Box>
  );
}

/** The row of headings the wide panel prints above its columns. */
function ColumnHeadings({ columns }: { columns: NavColumn[] }) {
  return (
    <Box sx={{ display: "flex", columnGap: "40px" }}>
      {columns.map((column) => (
        <Box
          key={column.heading}
          sx={{ width: 1, display: "flex", columnGap: LIST_GAP }}
        >
          <ColumnHeading heading={column.heading ?? ""} />
          {column.beside ? (
            <ColumnHeading heading={column.beside.heading} beside />
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

function ColumnBody({
  column,
  variant,
  onNavigate,
  sx,
}: {
  column: NavColumn;
  variant: RowVariant;
  onNavigate?: () => void;
  sx?: ReturnType<typeof columnMotion>;
}) {
  const heading = variant === "drawer" && column.heading;
  return (
    <Box
      sx={{
        ...COLUMN[variant],
        width: 1,
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      {heading ? <CapsHeading tight>{heading}</CapsHeading> : null}
      {column.blocks.map((block, blockIndex) => (
        <Block
          key={`${block.kind}-${blockIndex}`}
          block={block}
          variant={variant}
          onNavigate={onNavigate}
        />
      ))}
    </Box>
  );
}

/**
 * A column's second list. The drawer heads it as it heads the column; the
 * panel heads it here while the two are stacked, and in the row of headings
 * once they are side by side.
 */
function BesideList({
  list,
  variant,
  onNavigate,
}: {
  list: NonNullable<NavColumn["beside"]>;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  const block: NavBlock = { kind: "plain", items: list.items };
  if (variant === "drawer") {
    return (
      <ColumnBody
        column={{ heading: list.heading, blocks: [block] }}
        variant={variant}
        onNavigate={onNavigate}
      />
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ [SIDE_BY_SIDE.panel]: { display: "none" } }}>
        <CapsHeading>{list.heading}</CapsHeading>
      </Box>
      <Block block={block} variant={variant} onNavigate={onNavigate} />
    </Box>
  );
}

function MenuColumn({
  column,
  index,
  variant,
  onNavigate,
}: {
  column: NavColumn;
  index: number;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  const motion = columnMotion(index, variant);
  if (!column.beside) {
    return (
      <ColumnBody
        column={column}
        variant={variant}
        onNavigate={onNavigate}
        sx={motion}
      />
    );
  }
  // Stacked, the second list keeps the gap the column puts between blocks.
  return (
    <Box
      sx={{
        ...COLUMN[variant],
        width: 1,
        display: "flex",
        flexDirection: "column",
        [SIDE_BY_SIDE[variant]]: {
          flexDirection: "row",
          columnGap: LIST_GAP,
          "& > *": { flex: "1 1 0" },
        },
        ...motion,
      }}
    >
      <ColumnBody column={column} variant={variant} onNavigate={onNavigate} />
      <BesideList
        list={column.beside}
        variant={variant}
        onNavigate={onNavigate}
      />
    </Box>
  );
}

/**
 * One menu's body, shared by the wide panel and the drawer.
 *
 * The two differ in more than width: the drawer stacks the columns, moves each
 * column's heading from the rule across the top into the column itself, and
 * drops the summary line and the price caption from every row.
 */
export default function NavMenuContent({
  menu,
  variant,
  onNavigate,
}: {
  menu: NavMenu;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  const headings = menu.columns.filter(
    (column) => column.heading !== undefined
  );

  return (
    <Box sx={{ ...BODY[variant], display: "flex", flexDirection: "column" }}>
      {variant === "panel" && headings.length > 0 ? (
        <ColumnHeadings columns={headings} />
      ) : null}
      <Box
        sx={{
          ...COLUMNS_ROW[variant],
          display: "flex",
          alignItems: "stretch",
          justifyContent: "space-between",
        }}
      >
        {menu.columns.map((column, index) => (
          <MenuColumn
            key={`${menu.id}-${index}`}
            column={column}
            index={index}
            variant={variant}
            onNavigate={onNavigate}
          />
        ))}
      </Box>
      {menu.footer ? (
        <Box sx={columnMotion(menu.columns.length, variant)}>
          <MenuFooter
            footer={menu.footer}
            variant={variant}
            onNavigate={onNavigate}
          />
        </Box>
      ) : null}
    </Box>
  );
}
